import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { httpConfigApi, httpEndpointApi } from "../services/api";
import {
  HttpRequestConfig,
  HttpRequestConfigCreate,
  HttpRequestConfigFilters,
  HttpRequestConfigUpdate,
  HttpRequestEndpoint,
  HttpRequestEndpointCreate,
  HttpRequestEndpointFilters,
  HttpRequestEndpointUpdate,
} from "../types/http";

export const useHttpConfigs = (filters: HttpRequestConfigFilters = {}) => {
  return useQuery({
    queryKey: ["http-configs", filters],
    queryFn: () => httpConfigApi.getConfigs(filters),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useHttpConfig = (id: number) => {
  return useQuery({
    queryKey: ["http-config", id],
    queryFn: () => httpConfigApi.getConfig(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateHttpConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: HttpRequestConfigCreate) =>
      httpConfigApi.createConfig(data),

    onSuccess: (created: HttpRequestConfig) => {
      queryClient.invalidateQueries({
        queryKey: ["http-configs"],
      });
      toast.success(`Config "${created.name}" created successfully`);
    },

    onError: (error: any) => {
      const message = error.response?.data?.detail || "Failed to create config";
      toast.error(message);
    },
  });
};

export const useUpdateHttpConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: HttpRequestConfigUpdate }) =>
      httpConfigApi.updateConfig(id, data),

    onSuccess: (updated: HttpRequestConfig) => {
      queryClient.setQueryData(["http-config", updated.id], updated);
      queryClient.invalidateQueries({
        queryKey: ["http-configs"],
      });
      toast.success(`Config "${updated.name}" updated successfully`);
    },

    onError: (error: any) => {
      const message = error.response?.data?.detail || "Failed to update config";
      toast.error(message);
    },
  });
};

export const useDeleteHttpConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => httpConfigApi.deleteConfig(id),

    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({
        queryKey: ["http-config", deletedId],
      });
      queryClient.invalidateQueries({
        queryKey: ["http-configs"],
      });
      toast.success("Config deleted successfully");
    },

    onError: (error: any) => {
      const message = error.response?.data?.detail || "Failed to delete config";
      toast.error(message);
    },
  });
};

export const useHttpEndpoints = (filters: HttpRequestEndpointFilters = {}) => {
  return useQuery({
    queryKey: ["http-endpoints", filters],
    queryFn: () => httpEndpointApi.getEndpoints(filters),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useHttpEndpoint = (id: number) => {
  return useQuery({
    queryKey: ["http-endpoint", id],
    queryFn: () => httpEndpointApi.getEndpoint(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateHttpEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: HttpRequestEndpointCreate) =>
      httpEndpointApi.createEndpoint(data),

    onSuccess: (created: HttpRequestEndpoint) => {
      queryClient.invalidateQueries({
        queryKey: ["http-endpoints"],
      });
      toast.success(`Endpoint "${created.name}" created successfully`);
    },

    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to create endpoint";
      toast.error(message);
    },
  });
};

export const useUpdateHttpEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: HttpRequestEndpointUpdate;
    }) => httpEndpointApi.updateEndpoint(id, data),

    onSuccess: (updated: HttpRequestEndpoint) => {
      queryClient.setQueryData(["http-endpoint", updated.id], updated);
      queryClient.invalidateQueries({
        queryKey: ["http-endpoints"],
      });
      toast.success(`Endpoint "${updated.name}" updated successfully`);
    },

    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to update endpoint";
      toast.error(message);
    },
  });
};

export const useDeleteHttpEndpoint = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => httpEndpointApi.deleteEndpoint(id),

    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({
        queryKey: ["http-endpoint", deletedId],
      });
      queryClient.invalidateQueries({
        queryKey: ["http-endpoints"],
      });
      toast.success("Endpoint deleted successfully");
    },

    onError: (error: any) => {
      const message =
        error.response?.data?.detail || "Failed to delete endpoint";
      toast.error(message);
    },
  });
};
